export interface SQLGenerationResult {
  sql: string;
  dialect: string;
  assumptions: string[];
  validation: {
    tables_used: string[];
    columns_used: string[];
    schema_mismatches: string[];
  };
  parameters: {
    limit: number | null;
    filters: string[];
    order_by: string | null;
  };
  rationale: string;
  follow_up: string;
  header_mapping?: {
    original_headers: string[];
    mapped_headers: {
      table_name: string;
      column_name: string;
      data_type: string;
    };
    mapping_used: boolean;
  };
}

export interface APIError {
  error: string;
  details: string;
}

// Use relative URLs for Vercel deployment, fallback to Vite dev server proxy for development
const API_BASE_URL = window.location.hostname === 'localhost' 
  ? '' // Use relative URLs, Vite dev server will proxy /api requests
  : '';

export async function generateSQL(
  schemaFile: File,
  request: string,
  dialect: string = 'postgres',
  limit: string | null = null
): Promise<SQLGenerationResult> {
  // Read the CSV file content
  const schemaContent = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Failed to read schema file'));
    reader.readAsText(schemaFile);
  });

  const requestBody = {
    schema: schemaContent,
    request: request,
    dialect: dialect,
    limit: limit
  };

  console.log('API call details:', {
    url: `${API_BASE_URL}/api/generate-v2`,
    method: 'POST',
    body: {
      schema: schemaContent.substring(0, 100) + '...', // Show first 100 chars for debugging
      request,
      dialect,
      limit
    }
  });

  try {
    const response = await fetch(`${API_BASE_URL}/api/generate-v2`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    // Check if response has content before trying to parse JSON
    const text = await response.text();
    console.log('Response text length:', text.length);
    console.log('Response text (first 500 chars):', text.substring(0, 500));
    
    if (!text || text.trim() === '') {
      throw new Error('Server returned empty response');
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      console.error('JSON parse error:', parseError);
      console.error('Full response text:', text);
      
      // Check if it's an HTML error page
      if (text.includes('<html') || text.includes('<!DOCTYPE')) {
        throw new Error('Server returned HTML instead of JSON - this indicates a routing or deployment issue');
      }
      
      throw new Error(`Invalid JSON response: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`);
    }

    if (!response.ok) {
      console.error('API error response:', data);
      throw new Error(data.details || data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    console.log('API response parsed successfully:', data);
    return data;

  } catch (fetchError) {
    console.error('Fetch error:', fetchError);
    
    if (fetchError instanceof TypeError && fetchError.message.includes('Failed to fetch')) {
      throw new Error('Cannot connect to the server. Please check if the API endpoints are deployed correctly.');
    }
    
    throw fetchError;
  }
}

export async function healthCheck(): Promise<{ status: string; timestamp: string }> {
  console.log('Health check URL:', `${API_BASE_URL}/api/health`);
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/health`);
    
    console.log('Health check response status:', response.status);
    console.log('Health check response headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status} ${response.statusText}`);
    }

    const text = await response.text();
    console.log('Health check response text:', text);
    
    if (!text || text.trim() === '') {
      throw new Error('Health check returned empty response');
    }

    try {
      const result = JSON.parse(text);
      console.log('Health check parsed successfully:', result);
      return result;
    } catch (parseError) {
      console.error('Health check JSON parse error:', parseError);
      console.error('Health check response text:', text);
      
      if (text.includes('<html') || text.includes('<!DOCTYPE')) {
        throw new Error('Health check returned HTML instead of JSON - API endpoint not found');
      }
      
      throw new Error(`Invalid JSON in health check: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`);
    }
    
  } catch (fetchError) {
    console.error('Health check fetch error:', fetchError);
    
    if (fetchError instanceof TypeError && fetchError.message.includes('Failed to fetch')) {
      throw new Error('Cannot connect to health check endpoint. API may not be deployed.');
    }
    
    throw fetchError;
  }
}